/**
 * YUMIX — Deep Chat Onboarding (Jour 1, écrit)
 *
 * Iter. 411.mm20 — TEXT-ONLY OVERHAUL
 * ------------------------------------
 * Prod bug (Cédric, 2026-02-09): les users iOS étaient bloqués sur
 * "COACH YUMIX T'ÉCOUTE" à cause des permissions micro Safari qui
 * refusaient MediaRecorder même après acceptation.
 *
 * Fix radical par directive utilisateur :
 *   • Suppression complète de MediaRecorder, getUserMedia, /transcribe,
 *     et de tous les boutons "Passer / Skip" (l'étape est obligatoire).
 *   • Remplacement par un grand <textarea> avec bouton ENVOYER activé
 *     uniquement si le texte n'est pas vide.
 *   • Le texte est envoyé tel quel à POST /onboarding/deep-chat/reply
 *     (mêmes routes qu'avant, `user_answer` était déjà une string —
 *     l'audio ne servait qu'à produire cette string via Whisper).
 *   • Le backend persiste dans db.deep_chat_answers et alimente le
 *     prompt du LLM Coach exactement comme avant.
 *   • Le coach continue de RÉPONDRE en audio TTS (output audio OK,
 *     seulement l'input change).
 */
/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, api } from '../context/AppContext';
import { Button } from '../components/ui/button';
import {
  Loader2, Volume2, ChevronRight, Sparkles, AudioLines, Send, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import useCameraViewport from '../utils/useCameraViewport';

const STAGES = {
  INTRO: 'intro',
  PLAYING_QUESTION: 'playing_question',
  AWAITING_USER: 'awaiting_user',
  WAITING_REPLY: 'waiting_reply',
  PLAYING_REPLY: 'playing_reply',
  COMPLETING: 'completing',
};

const playB64Mp3 = (b64) => new Promise((resolve) => {
  // On iOS Safari, TTS playback can silently fail if the browser
  // hasn't seen a user gesture yet. We never *fail* the flow because
  // of audio — we resolve regardless so the UX keeps moving.
  if (!b64) return resolve();
  try {
    const audio = new Audio(`data:audio/mp3;base64,${b64}`);
    audio.onended = () => resolve();
    audio.onerror = () => resolve();
    audio.play().catch(() => resolve());
    // Belt & suspenders: force-resolve after 12s so a stuck decoder
    // never blocks the state machine.
    setTimeout(resolve, 12000);
  } catch {
    resolve();
  }
});

const DeepChatOnboarding = () => {
  const { user, language, updateUser } = useAuth();
  const navigate = useNavigate();
  const fr = (language || 'fr').startsWith('fr');

  const [stage, setStage] = useState(STAGES.INTRO);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userText, setUserText] = useState('');
  const [transcript, setTranscript] = useState('');   // last submitted answer (for display)
  const [coachReply, setCoachReply] = useState('');
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(3);

  const startedRef = useRef(false);
  const textareaRef = useRef(null);

  // Guard: skip if already done
  useEffect(() => {
    if (user?.deep_chat_completed) {
      navigate('/welcome-challenge-intro', { replace: true });
    }
  }, [user, navigate]);

  // Auto-focus the textarea when the user reaches the answer stage,
  // opens the mobile keyboard immediately so the flow feels natural.
  useEffect(() => {
    if (stage === STAGES.AWAITING_USER && textareaRef.current) {
      // Slight delay for the AnimatePresence transition to finish
      const t = setTimeout(() => {
        try { textareaRef.current.focus(); } catch { /* noop */ }
      }, 250);
      return () => clearTimeout(t);
    }
  }, [stage]);

  // ────────────────────────────────────────────────────────────
  // Start the deep chat (fetch first question + auto-play audio)
  // ────────────────────────────────────────────────────────────
  const startDeepChat = async () => {
    if (startedRef.current) return;
    startedRef.current = true;
    try {
      setStage(STAGES.PLAYING_QUESTION);
      const res = await api.post('/onboarding/deep-chat/start', { language: fr ? 'fr' : 'en' });
      const q = res.data;
      setCurrentQuestion(q);
      setQuestionNumber(q.question_id);
      setTotalQuestions(q.total_questions || 3);
      await playB64Mp3(q.audio_b64);
      setStage(STAGES.AWAITING_USER);
    } catch (err) {
      console.error('[DeepChat] start failed', err);
      toast.error(fr ? 'Impossible de démarrer la conversation. Réessaie.' : 'Could not start the chat. Retry.');
      startedRef.current = false;
      setStage(STAGES.INTRO);
    }
  };

  // ────────────────────────────────────────────────────────────
  // Submit typed answer → /reply → play coach reply → next question
  // ────────────────────────────────────────────────────────────
  const submitAnswer = async () => {
    const answer = (userText || '').trim();
    if (!answer) return; // guard — button is disabled but defence in depth
    setTranscript(answer);
    setUserText('');
    setStage(STAGES.WAITING_REPLY);
    try {
      const res = await api.post('/onboarding/deep-chat/reply', {
        question_id: questionNumber,
        user_answer: answer,
        language: fr ? 'fr' : 'en',
      });
      const data = res.data;
      setCoachReply(data.reply_text || '');
      setStage(STAGES.PLAYING_REPLY);
      await playB64Mp3(data.reply_audio_b64);

      if (data.is_last) {
        await completeDeepChat();
      } else if (data.next_question) {
        // Breath between the coach reply and the next question
        await new Promise((r) => setTimeout(r, 600));
        setTranscript('');
        setCoachReply('');
        setCurrentQuestion(data.next_question);
        setQuestionNumber(data.next_question.question_id);
        setStage(STAGES.PLAYING_QUESTION);
        await playB64Mp3(data.next_question.audio_b64);
        setStage(STAGES.AWAITING_USER);
      }
    } catch (err) {
      console.error('[DeepChat] reply failed', err);
      toast.error(fr ? 'Erreur de réponse coach. Réessaie.' : 'Coach reply failed. Retry.');
      // Restore the user's text so they can retry without re-typing
      setUserText(answer);
      setStage(STAGES.AWAITING_USER);
    }
  };

  const completeDeepChat = async () => {
    setStage(STAGES.COMPLETING);
    try {
      await api.post('/onboarding/deep-chat/complete');
      try { updateUser?.({ ...user, deep_chat_completed: true }); } catch { /* silent */ }
      toast.success(fr ? 'Première mission validée · on enchaîne' : 'First mission cleared · keep going');
      navigate('/welcome-challenge-intro', { replace: true });
    } catch (err) {
      console.error('[DeepChat] complete failed', err);
      toast.error(fr ? 'Erreur de finalisation. Réessaie.' : 'Could not finalize. Retry.');
      setStage(STAGES.AWAITING_USER);
    }
  };

  // ────────────────────────────────────────────────────────────
  // Render helpers
  // ────────────────────────────────────────────────────────────
  const isAgentSpeaking = stage === STAGES.PLAYING_QUESTION || stage === STAGES.PLAYING_REPLY;
  const isThinking = stage === STAGES.WAITING_REPLY || stage === STAGES.COMPLETING;
  const canAnswer = stage === STAGES.AWAITING_USER;
  const canSubmit = canAnswer && (userText || '').trim().length > 0;

  const { containerStyle } = useCameraViewport({ open: true, allowInternalScroll: true });

  return (
    <div
      className="flex items-start justify-center bg-background relative px-4 py-8"
      style={containerStyle}
      data-testid="deep-chat-page"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-orange-500/15 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="relative w-full max-w-md mx-auto z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-orange-400 font-bold">
            {fr ? 'Consultation Jour 1' : 'Day 1 Consultation'}
          </p>
          <h1 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-foreground mt-2 leading-tight">
            {fr ? 'Coach YUMIX t\'écoute' : 'Coach YUMIX is listening'}
          </h1>
          {stage !== STAGES.INTRO && (
            <p className="text-xs text-muted-foreground mt-2 tabular-nums">
              {fr ? 'Question' : 'Question'} {questionNumber} / {totalQuestions}
            </p>
          )}
        </motion.div>

        {/* Coach orb visualizer */}
        <div className="flex justify-center mb-6">
          <CoachOrb speaking={isAgentSpeaking} thinking={isThinking} />
        </div>

        {/* Body */}
        <AnimatePresence mode="wait">
          {stage === STAGES.INTRO && (
            <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="rounded-2xl border border-orange-500/30 bg-card/60 backdrop-blur-md p-5 mb-5">
                <p className="text-sm text-foreground/90 leading-relaxed">
                  {fr
                    ? <>Avant qu'on attaque, je veux te <strong className="text-orange-400">comprendre</strong>. Trois questions, deux minutes.</>
                    : <>Before we hit the ground, I want to <strong className="text-orange-400">understand</strong> you. Three questions, two minutes.</>}
                </p>
                <p className="text-xs text-muted-foreground mt-3">
                  {fr
                    ? '⌨️ Tu écris tes réponses. Je te lis, je te réponds, et on cadre ta mission.'
                    : '⌨️ You type your answers. I read, I reply, and we lock in your mission.'}
                </p>
              </div>
              <Button
                onClick={startDeepChat}
                className="w-full h-14 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-heading uppercase tracking-[0.15em] text-sm shadow-xl shadow-orange-500/30"
                data-testid="deep-chat-start-btn"
              >
                <AudioLines className="w-5 h-5 mr-2" />
                {fr ? 'Démarrer la consultation' : 'Start the consultation'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-center text-[10px] text-muted-foreground mt-3 flex items-center justify-center gap-1">
                <Sparkles className="w-3 h-3 text-orange-400" />
                {fr ? 'Étape obligatoire — deux minutes chrono.' : 'Mandatory step — two minutes.'}
              </p>
            </motion.div>
          )}

          {(stage === STAGES.PLAYING_QUESTION || stage === STAGES.AWAITING_USER) && currentQuestion && (
            <motion.div key="question" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="rounded-2xl border border-orange-500/30 bg-card/60 backdrop-blur-md p-5 mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <Volume2 className="w-3.5 h-3.5 text-orange-400" />
                  <span className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">
                    {fr ? 'Coach YUMIX' : 'Coach YUMIX'}
                  </span>
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed" data-testid="deep-chat-question-text">
                  {currentQuestion.text}
                </p>
              </div>

              {canAnswer && (
                <>
                  <div className="rounded-2xl border border-orange-500/30 bg-black/40 backdrop-blur-md p-4 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageSquare className="w-3.5 h-3.5 text-orange-400" />
                      <span className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">
                        {fr ? 'Ta réponse' : 'Your answer'}
                      </span>
                    </div>
                    <textarea
                      ref={textareaRef}
                      value={userText}
                      onChange={(e) => setUserText(e.target.value)}
                      onKeyDown={(e) => {
                        // Cmd/Ctrl + Enter → submit for desktop power users
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canSubmit) {
                          e.preventDefault();
                          submitAnswer();
                        }
                      }}
                      placeholder={fr
                        ? 'Écris librement, prends ton temps…'
                        : 'Write freely, take your time…'}
                      rows={5}
                      maxLength={2000}
                      autoCorrect="on"
                      autoCapitalize="sentences"
                      spellCheck
                      data-testid="deep-chat-text-input"
                      className="w-full bg-transparent text-foreground/95 text-sm leading-relaxed placeholder:text-muted-foreground/60 focus:outline-none resize-none"
                    />
                    <div className="flex items-center justify-end mt-1">
                      <span
                        className="text-[10px] text-muted-foreground/60 tabular-nums"
                        data-testid="deep-chat-char-count"
                      >
                        {userText.length}/2000
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={submitAnswer}
                    disabled={!canSubmit}
                    className={`w-full h-14 font-heading uppercase tracking-[0.15em] text-sm transition-all ${
                      canSubmit
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-xl shadow-orange-500/30'
                        : 'bg-secondary/50 text-muted-foreground cursor-not-allowed'
                    }`}
                    data-testid="deep-chat-submit-btn"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {fr ? 'Envoyer' : 'Send'}
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </>
              )}

              {stage === STAGES.PLAYING_QUESTION && (
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-3">
                  <Volume2 className="w-3.5 h-3.5 text-orange-400 animate-pulse" />
                  {fr ? 'Le coach parle…' : 'Coach speaking…'}
                </div>
              )}
            </motion.div>
          )}

          {stage === STAGES.WAITING_REPLY && (
            <motion.div key="thinking" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
              {transcript && (
                <div className="rounded-xl border border-border/50 bg-secondary/30 p-4">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    {fr ? 'Toi' : 'You'}
                  </p>
                  <p className="text-sm text-foreground/85" data-testid="deep-chat-transcript">{transcript}</p>
                </div>
              )}
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                {fr ? 'Le coach réfléchit…' : 'Coach is thinking…'}
              </div>
            </motion.div>
          )}

          {stage === STAGES.PLAYING_REPLY && (
            <motion.div key="reply" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
              {transcript && (
                <div className="rounded-xl border border-border/50 bg-secondary/30 p-4">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                    {fr ? 'Toi' : 'You'}
                  </p>
                  <p className="text-sm text-foreground/85">{transcript}</p>
                </div>
              )}
              <div className="rounded-2xl border border-orange-500/30 bg-card/60 backdrop-blur-md p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Volume2 className="w-3.5 h-3.5 text-orange-400 animate-pulse" />
                  <span className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">
                    {fr ? 'Coach YUMIX' : 'Coach YUMIX'}
                  </span>
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed" data-testid="deep-chat-reply-text">{coachReply}</p>
              </div>
            </motion.div>
          )}

          {stage === STAGES.COMPLETING && (
            <motion.div key="completing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
              <p className="text-sm text-muted-foreground">
                {fr ? 'On enchaîne sur ta mission Jour 7…' : 'Locking in your Day 7 mission…'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Pulsing orb visualizer for the coach voice
// ────────────────────────────────────────────────────────────
const CoachOrb = ({ speaking, thinking }) => {
  const ring = speaking ? 'border-orange-400/80' : thinking ? 'border-amber-400/60' : 'border-orange-500/40';
  const inner = speaking ? 'from-orange-500 to-amber-500' : 'from-orange-600 to-orange-700';
  return (
    <div className={`relative w-32 h-32 rounded-full border-4 ${ring} flex items-center justify-center transition-colors`}>
      {(speaking || thinking) && (
        <div className={`absolute inset-0 rounded-full border-2 ${ring} animate-ping opacity-60`} />
      )}
      <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${inner} shadow-2xl shadow-orange-500/40 flex items-center justify-center`}>
        <AudioLines className={`w-10 h-10 text-white ${speaking ? 'animate-pulse' : ''}`} />
      </div>
    </div>
  );
};

export default DeepChatOnboarding;
